import React, { createContext, useState, useEffect, useContext } from "react";

interface UIContextProps {
  isMobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  isCartOpen: boolean;
  setCartOpen: (open: boolean) => void;
  toggleMobileMenu: () => void;
  toggleCart: () => void;
}

export const UIContext = createContext<UIContextProps>({
  isMobileMenuOpen: false,
  setMobileMenuOpen: () => {},
  isCartOpen: false,
  setCartOpen: () => {},
  toggleMobileMenu: () => {},
  toggleCart: () => {},
});

export const useUI = () => useContext(UIContext);

export const UIProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isCartOpen, setCartOpen] = useState(false);

  // Centralized body scroll locking
  useEffect(() => {
    if (isMobileMenuOpen || isCartOpen) {
      // Save the current scroll position and overflow style
      const scrollY = window.scrollY;
      const originalOverflow = document.body.style.overflow || "";

      // Lock scrolling
      document.body.style.overflow = "hidden";

      // Restore on cleanup
      return () => {
        document.body.style.overflow = originalOverflow;
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMobileMenuOpen, isCartOpen]);

  const toggleMobileMenu = () => setMobileMenuOpen((prev) => !prev);
  const toggleCart = () => setCartOpen((prev) => !prev);

  return (
    <UIContext.Provider
      value={{
        isMobileMenuOpen,
        setMobileMenuOpen,
        isCartOpen,
        setCartOpen,
        toggleMobileMenu,
        toggleCart,
      }}
    >
      {children}
    </UIContext.Provider>
  );
};
